import type { Flow } from '../../contract/flow';
import type { AsyncFlow, AsyncFlowState } from '../../contract/async-flow';
import type { FlowSubscription } from '../../contract/subscription';
import type { SyncFlowGate, SyncFlowValue } from '../../contract/sync-read';
import { withBypassSsr } from '../../utils/bypassSsrCheck';
import { FlowComputationBase } from '../base/computation';
import { FlowAwait } from './flowAwait';

/**
 * Объект контекста, предоставляемый функциям вычисления асинхронного потока.
 */
export interface AsyncFlowComputationContext {
    /**
     * Синхронно читает текущее значение из потока.
     *
     * Этот метод устанавливает зависимость между текущим вычислением
     * и предоставленным потоком, гарантируя, что изменения потока вызовут пересчет.
     *
     * Асинхронный поток (`AsyncFlow`) сюда передать нельзя — гейт {@link SyncFlowGate}
     * дает ошибку типов. Синхронное чтение асинхронного потока заставило бы вручную
     * разбирать `pending`/`error` и ломало бы Suspense/SSR-поведение у потребителей.
     * Для асинхронных источников есть {@link AsyncFlowComputationContext.watchAsync}.
     *
     * В обобщенном хелпере параметр объявляйте как `Flow<Data>`: гейт считается по типу
     * аргумента, и для еще не выведенного типа-параметра его вычислить нельзя.
     *
     * @typeParam TFlow - Тип потока, из которого читается значение
     * @param flow - Поток, из которого читается значение
     * @returns Текущее значение потока
     * @throws Выбросит ошибку, если поток находится в состоянии ошибки
     *
     * @example
     * ```typescript
     * const value = watch(someFlow); // Читает текущее значение и создает зависимость
     * ```
     */
    readonly watch: <TFlow extends Flow<unknown>>(flow: TFlow & SyncFlowGate<TFlow>) => SyncFlowValue<TFlow>;

    /**
     * Утилиты для отслеживания и ожидания асинхронных источников потоков.
     *
     * В геттере (`yield*`) любой из этих методов завершается синхронно, если
     * результат уже однозначно следует из текущих состояний источников — вычисление не
     * уходит в `pending` на микротаск.
     *
     * Условие синхронного завершения для каждого метода совпадает с семантикой
     * соответствующего `Promise.*`: уже разрешенные источники срабатывают раньше pending,
     * а между собой — в порядке индексов.
     */
    readonly watchAsync: {
        /**
         * Асинхронно читает значение из асинхронного потока.
         *
         * Этот метод устанавливает зависимость между текущим вычислением
         * и предоставленным асинхронным потоком, гарантируя, что изменения потока вызовут пересчет.
         *
         * Завершается синхронно, если поток уже в `success` или `error`.
         *
         * @param flow - Асинхронный поток, из которого читается значение
         * @returns Promise generator, который разрешается в текущее значение асинхронного потока
         * @throws Выбросит ошибку, если поток находится в состоянии ошибки
         *
         * @example
         * ```typescript
         * const userData = yield* watchAsync(userFlow); // Читает значение и создает зависимость
         * ```
         */
        <T>(flow: AsyncFlow<T>): PromiseGenerator<T>;

        /**
         * Ожидает вычисления всех асинхронных потоков, аналогично Promise.all.
         *
         * Завершается синхронно ошибкой, если хотя бы один источник в `error` (берется
         * первый по индексу), и синхронно успехом, если все источники в `success`.
         *
         * @param flows - Массив асинхронных потоков для ожидания
         * @returns Promise generator, который разрешается в массив всех значений потоков
         * @throws Выбросит ошибку, если какой-либо из потоков завершится ошибкой
         */
        all<T extends readonly AsyncFlow<unknown>[] | []>(
            flows: T
        ): PromiseGenerator<{ -readonly [K in keyof T]: AwaitedFlow<T[K]> }>;

        /**
         * Ожидает вычисления всех асинхронных потоков, аналогично Promise.allSettled.
         *
         * Завершается синхронно, если ни один источник не в `pending`.
         *
         * @param flows - Массив асинхронных потоков для ожидания
         * @returns Promise generator, который разрешается в массив завершенных результатов
         */
        allSettled<T extends readonly AsyncFlow<unknown>[] | []>(
            flows: T
        ): PromiseGenerator<{ -readonly [K in keyof T]: PromiseSettledResult<AwaitedFlow<T[K]>> }>;

        /**
         * Ожидает вычисления любого асинхронного потока, аналогично Promise.any.
         *
         * Завершается синхронно успехом, если хотя бы один источник в `success` (берется
         * первый по индексу), и синхронно ошибкой `AggregateError`, если все источники
         * в `error`. Порядок `AggregateError.errors` — порядок индексов источников.
         *
         * @param flows - Массив асинхронных потоков
         * @returns Promise generator, который разрешается в первое успешное значение потока
         * @throws Выбросит ошибку, если все потоки отклонятся
         */
        any<T extends readonly AsyncFlow<unknown>[] | []>(flows: T): PromiseGenerator<AwaitedFlow<T[number]>>;

        /**
         * Ожидает первого вычисления любого асинхронного потока, аналогично Promise.race.
         *
         * Завершается синхронно результатом первого по индексу разрешенного источника —
         * значением при `success`, ошибкой при `error`.
         *
         * @param flows - Массив асинхронных потоков
         * @returns Promise generator, который разрешается в первое завершенное значение потока
         */
        race<T extends readonly AsyncFlow<unknown>[] | []>(flows: T): PromiseGenerator<AwaitedFlow<T[number]>>;
    };

    /**
     * Отменяет текущее вычисление и предотвращает обновление значения потока.
     *
     * При вызове этот метод прерывает текущий процесс вычисления. Поток
     * сохранит свое предыдущее значение.
     *
     * @returns Никогда не возвращается - всегда выбрасывает исключение для прерывания вычисления
     * @throws Всегда выбрасывает сигнал прерывания для отмены вычисления
     *
     * @example
     * ```typescript
     * if (shouldSkipUpdate) {
     *     skip(); // Вычисление отменено, значение потока не изменено
     * }
     * ```
     */
    readonly skip: () => never;

    /**
     * Сигнал прерывания для отмены текущего асинхронного вычисления.
     *
     * Этот сигнал будет активирован, когда вычисление прервано или завершено,
     * позволяя асинхронным операциям отменяться корректно.
     */
    readonly signal: AbortSignal;
}

/**
 * Вспомогательный тип, который извлекает тип значения из AsyncFlow.
 */
type AwaitedFlow<T> = T extends AsyncFlow<infer U> ? U : never;

/**
 * Promise, который также может использоваться как итератор для асинхронных операций на основе генераторов.
 *
 * Итератор отдает наружу {@link FlowAwait} — токен, который умеет распаковать только драйвер вычисления.
 */
type PromiseGenerator<T> = Promise<T> & Iterable<FlowAwait, T, undefined>;

/**
 * Представляет успешно выполненный результат промиса.
 */
interface PromiseFulfilledResult<T> {
    status: 'fulfilled';
    value: T;
}

/**
 * Представляет отклоненный результат промиса.
 */
interface PromiseRejectedResult {
    status: 'rejected';
    reason: unknown;
}

/**
 * Объединенный тип, представляющий либо выполненный, либо отклоненный результат промиса.
 */
type PromiseSettledResult<T> = PromiseFulfilledResult<T> | PromiseRejectedResult;

/**
 * Реализация вычисления асинхронного потока.
 *
 * @typeParam T - Тип значения, производимого этим вычислением
 */
export class AsyncFlowComputation<T> extends FlowComputationBase<AsyncFlowState<T>, AsyncFlowComputationContext> {
    /**
     * Контроллер для прерывания асинхронного вычисления при необходимости.
     */
    protected abortController: AbortController;

    /**
     * Номер эпохи для этого вычисления, используется для отслеживания устаревших вычислений.
     */
    public readonly epoch: number;

    /**
     * Создает новый экземпляр AsyncFlowComputation.
     *
     * @param epoch - Номер эпохи для этого вычисления
     */
    public constructor(epoch: number) {
        super();
        this.abortController = new AbortController();
        this.epoch = epoch;
    }

    /**
     * Создает и возвращает контекст вычисления.
     *
     * @returns Объект контекста
     */
    public getContext(): AsyncFlowComputationContext {
        const watchAsync: AsyncFlowComputationContext['watchAsync'] = flow => {
            return this.watchAsyncFlow(flow);
        };

        watchAsync.all = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const result = this.watchAsyncCombinator(
                flows,
                snapshots => {
                    const values: unknown[] = [];
                    let hasPending = false;
                    for (const snapshot of snapshots) {
                        if (snapshot.status === 'error') {
                            return { ok: false, error: snapshot.error };
                        }
                        if (snapshot.status === 'pending') {
                            hasPending = true;
                        } else {
                            values.push(snapshot.data);
                        }
                    }
                    return hasPending ? null : { ok: true, value: values };
                },
                perSource => Promise.all(perSource)
            );
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.all
            return result as PromiseGenerator<{ -readonly [K in keyof T]: AwaitedFlow<T[K]> }>;
        };

        watchAsync.allSettled = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const result = this.watchAsyncCombinator(
                flows,
                snapshots => {
                    if (snapshots.some(snapshot => snapshot.status === 'pending')) {
                        return null;
                    }
                    return {
                        ok: true,
                        value: snapshots.map(snapshot =>
                            snapshot.status === 'success'
                                ? { status: 'fulfilled', value: snapshot.data }
                                : {
                                      status: 'rejected',
                                      reason: snapshot.status === 'error' ? snapshot.error : undefined,
                                  }
                        ),
                    };
                },
                perSource => Promise.allSettled(perSource)
            );
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.allSettled
            return result as PromiseGenerator<{ -readonly [K in keyof T]: PromiseSettledResult<AwaitedFlow<T[K]>> }>;
        };

        watchAsync.any = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const result = this.watchAsyncCombinator(
                flows,
                snapshots => {
                    const errors: unknown[] = [];
                    let hasPending = false;
                    for (const snapshot of snapshots) {
                        if (snapshot.status === 'success') {
                            return { ok: true, value: snapshot.data };
                        }
                        if (snapshot.status === 'pending') {
                            hasPending = true;
                        } else {
                            errors.push(snapshot.error);
                        }
                    }
                    return hasPending
                        ? null
                        : { ok: false, error: new AggregateError(errors, 'All promises were rejected') };
                },
                perSource => Promise.any(perSource)
            );
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.any
            return result as PromiseGenerator<AwaitedFlow<T[number]>>;
        };

        watchAsync.race = <T extends readonly AsyncFlow<unknown>[] | []>(flows: T) => {
            const result = this.watchAsyncCombinator(
                flows,
                snapshots => {
                    for (const snapshot of snapshots) {
                        if (snapshot.status === 'success') {
                            return { ok: true, value: snapshot.data };
                        }
                        if (snapshot.status === 'error') {
                            return { ok: false, error: snapshot.error };
                        }
                    }
                    return null;
                },
                perSource => Promise.race(perSource)
            );
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- приводим типы в соответствие с Promise.race
            return result as PromiseGenerator<AwaitedFlow<T[number]>>;
        };

        return {
            watch: <TFlow extends Flow<unknown>>(flow: TFlow & SyncFlowGate<TFlow>) =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- гейт разрывает связь TFlow с типом данных потока, восстанавливаем ее вручную
                this.readFlow(flow) as SyncFlowValue<TFlow>,
            watchAsync,
            skip: () => this.skip(),
            signal: this.abortController.signal,
        };
    }

    /**
     * Завершает вычисление и прерывает любые текущие операции.
     * Этот метод сигнализирует о завершении выполнения функции-геттера.
     */
    public override finalize() {
        super.finalize();
        this.abortController.abort();
    }

    /**
     * Прерывает вычисление.
     */
    public abort() {
        this.abortController.abort();
    }

    /**
     * Синхронно читает текущее состояние источника и регистрирует его как зависимость.
     *
     * @param flow - Асинхронный поток-источник
     * @returns Текущее состояние источника
     */
    private readSourceSnapshot<T>(flow: AsyncFlow<T>): AsyncFlowState<T> {
        this.addSource(flow);
        const snapshot = flow.getSnapshot();
        this.setSourceValue(flow, snapshot);
        return snapshot;
    }

    /**
     * Строит PromiseGenerator для уже разрешенного результата: генераторный итератор синхронно
     * возвращает значение (`success`) или бросает ошибку (`error`), не уходя в pending.
     *
     * @param result - Разрешенный результат (значение или ошибка)
     * @returns PromiseGenerator, завершающийся синхронно
     */
    private settledIterator<R>(result: { ok: true; value: R } | { ok: false; error: unknown }): PromiseGenerator<R> {
        let promise: Promise<R>;
        if (result.ok) {
            promise = Promise.resolve(result.value);
        } else {
            promise = Promise.reject(result.error);
            // Генераторный путь бросает ошибку синхронно и не подписывается на этот промис,
            // поэтому вешаем no-op обработчик, чтобы не словить unhandled rejection.
            promise.catch(() => {});
        }

        return Object.assign(promise, {
            [Symbol.iterator]: function* (): Generator<FlowAwait, R, undefined> {
                if (result.ok) {
                    return result.value;
                }
                throw result.error;
            },
        });
    }

    /**
     * Читает одиночный асинхронный поток с синхронным fast-path.
     *
     * Если источник уже разрешен (success/error), генераторный итератор завершается
     * синхронно (`return`/`throw`) без ухода в pending на один микротаск. Promise-грань
     * результата сохраняется как часть контракта `PromiseGenerator`.
     *
     * @param flow - Асинхронный поток, из которого читается значение
     * @returns PromiseGenerator текущего значения потока
     */
    private watchAsyncFlow<T>(flow: AsyncFlow<T>): PromiseGenerator<T> {
        const snapshot = this.readSourceSnapshot(flow);

        if (snapshot.status === 'success') {
            return this.settledIterator({ ok: true, value: snapshot.data });
        }
        if (snapshot.status === 'error') {
            return this.settledIterator<T>({ ok: false, error: snapshot.error });
        }

        const promise = this.awaitAsyncFlow(flow);
        // Хендл может быть создан, но так и не итерирован (например, геттер бросил ошибку
        // до `yield*`), поэтому гасим потенциальный unhandled rejection.
        promise.catch(() => {});
        return this.toIterator(promise);
    }

    /**
     * Дожидается разрешения асинхронного потока и обновляет кешированное значение источника.
     *
     * Возвращаемое значение и записанный в кеш снапшот всегда берутся из одного чтения
     * `getSnapshot()`. Иначе источник может смениться между разрешением промиса и
     * продолжением `await`: вычисление построит результат из старых данных, но запишет в кеш
     * свежий снапшот — изменения источника больше не будет видно и значение потока залипнет.
     *
     * Зависимость должна быть уже зарегистрирована (`readSourceSnapshot`) — метод только
     * дожидается результата и обновляет кеш, но не вызывает `addSource`.
     *
     * @param flow - Асинхронный поток-источник
     * @returns Promise с разрешенным значением потока
     */
    private async awaitAsyncFlow<T>(flow: AsyncFlow<T>): Promise<T> {
        for (;;) {
            try {
                await flow.asPromise();
            } catch {
                // Результат ожидания берем только из снапшота, поэтому ошибка промиса тут не нужна
            }

            const snapshot = flow.getSnapshot();

            if (snapshot.status !== 'pending') {
                this.setSourceValue(flow, snapshot);
                if (snapshot.status === 'error') {
                    throw snapshot.error;
                }
                return snapshot.data;
            }

            // Если на момент продолжения источник снова в pending, ждем дальше: результат для
            // прошлого ожидания уже неактуален, а согласованную пару значение/снапшот даст
            // только следующее разрешенное состояние.
            await this.nextFlowChange(flow);
        }
    }

    /**
     * Ожидает ближайшего уведомления от потока через одноразовую подписку.
     *
     * Подписка снимается на первом уведомлении или при прерывании вычисления. При прерывании
     * ожидание завершается причиной прерывания: резолв здесь недопустим, иначе вычисление с
     * закешированным разрешенным `asPromise()` и pending-источником крутилось бы в цикле
     * микротасков.
     *
     * @param flow - Поток, изменения которого ожидаем
     * @returns Promise, разрешающийся на первом уведомлении
     */
    private nextFlowChange(flow: Flow<unknown>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const { signal } = this.abortController;
            if (signal.aborted) {
                reject(signal.reason);
                return;
            }

            let subscription: FlowSubscription | null = null;
            let settled = false;

            const settle = (complete: () => void) => {
                if (settled) {
                    return;
                }
                settled = true;
                signal.removeEventListener('abort', onAbort);
                subscription?.unsubscribe();
                complete();
            };
            const onAbort = () => settle(() => reject(signal.reason));
            const onChange = () => settle(resolve);

            signal.addEventListener('abort', onAbort);
            subscription = withBypassSsr(() => flow.subscribe(onChange));
            if (settled) {
                // уведомление пришло синхронно внутри subscribe — подписка снимается здесь
                subscription.unsubscribe();
            }
        });
    }

    /**
     * Строит промис по уже прочитанному состоянию источника без повторной регистрации зависимости.
     *
     * @param flow - Асинхронный поток-источник
     * @param snapshot - Ранее прочитанное состояние источника
     * @returns Promise с разрешенным значением потока
     */
    private sourceToPromise<T>(flow: AsyncFlow<T>, snapshot: AsyncFlowState<T>): Promise<T> {
        if (snapshot.status === 'success') {
            return Promise.resolve(snapshot.data);
        }
        if (snapshot.status === 'error') {
            return Promise.reject(snapshot.error);
        }
        return this.awaitAsyncFlow(flow);
    }

    /**
     * Реализует комбинатор watchAsync (all/allSettled/any/race) с синхронным fast-path.
     *
     * Результат собирается синхронно всегда, когда он уже однозначно следует из текущих
     * снапшотов источников по семантике соответствующего `Promise.*` (уже разрешенные
     * источники срабатывают раньше pending, а между собой — в порядке индексов). Если
     * `decideResult` возвращает `null`, результат синхронно не определим и используется
     * асинхронный путь на основе `Promise.*` — он дает тот же результат, просто позже.
     *
     * Зависимости регистрируются для всех источников (включая pending) до принятия решения.
     *
     * @param flows - Источники комбинатора
     * @param decideResult - Синхронное решение по снапшотам источников; `null` — не решаемо
     * @param asyncResult - Асинхронная сборка результата (Promise.all/allSettled/any/race)
     * @returns PromiseGenerator результата комбинатора
     */
    private watchAsyncCombinator(
        flows: readonly AsyncFlow<unknown>[],
        decideResult: (
            snapshots: AsyncFlowState<unknown>[]
        ) => { ok: true; value: unknown } | { ok: false; error: unknown } | null,
        asyncResult: (perSource: Promise<unknown>[]) => Promise<unknown>
    ): PromiseGenerator<unknown> {
        const snapshots = flows.map(flow => this.readSourceSnapshot(flow));

        const decided = decideResult(snapshots);
        if (decided !== null) {
            return this.settledIterator(decided);
        }

        const perSource = snapshots.map((snapshot, index) =>
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- индекс гарантировано существует
            this.sourceToPromise(flows[index]!, snapshot)
        );
        const promise = asyncResult(perSource);
        // Хендл может быть создан, но так и не итерирован — гасим потенциальный unhandled rejection.
        promise.catch(() => {});
        return this.toIterator(promise);
    }

    /**
     * Преобразует Promise в PromiseGenerator, который может использоваться с синтаксисом генераторов.
     *
     * Этот метод создает гибридный объект, который ведет себя и как Promise, и как
     * итерируемый генератор, позволяя использовать его с функциями-генераторами
     * и выражениями `yield*`.
     *
     * @typeParam T - Тип значения, в которое разрешается промис
     * @param promise - Промис для преобразования
     * @returns Promise generator, который может быть использован yield* в функциях-генераторах
     */
    private toIterator<T>(promise: Promise<T>): PromiseGenerator<T> {
        return Object.assign(promise, {
            [Symbol.iterator]: function* (): Generator<FlowAwait, T, undefined> {
                let value: { current: T };
                const iteratorPromise = promise.then(data => {
                    value = { current: data };
                    return data;
                });
                yield new FlowAwait(iteratorPromise);
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value гарантировано существует, так как yield ждет разрешения промиса
                return value!.current;
            },
        });
    }
}
